/**
 * Preferências de voz: a FORMA delas, não onde ficam guardadas.
 *
 * São por DISPOSITIVO e não por conta, e é por isso que não vivem no
 * servidor: o microfone que uso no portátil não é o que uso no telemóvel, e
 * sincronizar ids de dispositivo entre máquinas só produziria selecções
 * inválidas.
 *
 * A persistência é de quem hospeda — `localStorage` no browser,
 * `AsyncStorage` no telemóvel — porque uma é síncrona e a outra não, e
 * esconder essa diferença atrás de uma interface comum obrigaria o lado
 * síncrono a fingir que não é.
 */

export type VoiceMode = 'voice' | 'ptt';

export interface VoiceSettings {
  /** `null` = o que o browser escolher. */
  inputDeviceId: string | null;
  outputDeviceId: string | null;
  cameraDeviceId: string | null;
  /**
   * Resolução com que a câmara CAPTURA.
   *
   * Distinta da camada que cada espectador recebe: o simulcast envia sempre
   * três camadas derivadas desta. Capturar a 720p e deixar o SFU escolher é
   * o certo quando há banda; a 360p poupa CPU de codificação em portáteis
   * modestos, onde três camadas de 720p se sentem no ventilador.
   */
  videoProfile: 'low' | 'medium' | 'high';
  /** 'text' = 1080p/5fps nítido · 'motion' = 720p/30fps fluido. */
  screenProfile: 'text' | 'motion';
  mode: VoiceMode;
  /** 0-100. Ver `vadThresholds`. */
  sensitivity: number;
  /** `KeyboardEvent.code` — `code` e não `key`, para não depender do layout. */
  pttKey: string;
  /** userId → 0-100 (%). */
  volumes: Record<string, number>;
  /** userIds silenciados só para mim. */
  localMutes: string[];
}

export const DEFAULT_SETTINGS: VoiceSettings = {
  inputDeviceId: null,
  outputDeviceId: null,
  cameraDeviceId: null,
  videoProfile: 'medium',
  screenProfile: 'text',
  mode: 'voice',
  sensitivity: 50,
  pttKey: 'Space',
  volumes: {},
  localMutes: [],
};

/** A chave sob a qual as apps guardam isto. Uma só, para não divergirem. */
export const SETTINGS_KEY = 'tasca:voice-settings';

/**
 * Junta o que foi lido aos valores por omissão.
 *
 * Mesclar e não confiar no guardado: uma versão antiga do objecto não deve
 * deixar campos a `undefined` — e deixaria, porque o que foi gravado há dois
 * meses não conhece os campos de hoje.
 */
export function mergeSettings(stored: Partial<VoiceSettings> | null): VoiceSettings {
  if (!stored) return { ...DEFAULT_SETTINGS };
  return {
    ...DEFAULT_SETTINGS,
    ...stored,
    volumes: stored.volumes ?? {},
    localMutes: stored.localMutes ?? [],
  };
}

/**
 * Sensibilidade (0-100) → limiares de RMS para o detector de fala.
 *
 * Dois limiares, não um: com um só, o indicador pisca em cada pausa entre
 * palavras. O de saída é mais baixo que o de entrada (histerese), e ainda
 * leva um atraso de ~400ms em cima — ver `client.ts`.
 *
 * Sensibilidade alta = limiar baixo = apanha sussurros e também o
 * ventilador. 50 é o meio, calibrado para fala normal a 30-40cm do micro.
 */
export function vadThresholds(sensitivity: number): { enter: number; exit: number } {
  const clamped = Math.min(100, Math.max(0, sensitivity));
  const enter = Math.max(4, 36 - clamped * 0.36);
  return { enter, exit: enter * 0.55 };
}
